<template>
  <div
    class="px-15 group relative rounded-10"
    :class="isDarkMode ? 'hover:bg-project' : 'hover:bg-grey-200'"
  >
    <div
      class="py-19"
      :class="[{'border-t': hasBorder}, isDarkMode ? 'border-sidebar' : 'border-grey-500']"
    >
      <div class="flex justify-between">
        <div class="flex items-center">
          <p class="font-bold">{{ title }}</p>
          <span v-if="notification.status === 'unread'" class="w-8 h-8 rounded-full bg-brand ml-15" />
        </div>
        <p class="text-grey-250 text-14" :title="titleTime">{{ getTime }}</p>
      </div>
      <div class="flex items-stretch mt-12">
        <div class="flex-shrink-0 w-4 rounded-2 min-h-full mr-15" :style="userColor" />
        <div class="pr-30 text-15">
          <div ref="descriptionRef" class="text-grey-975 line-clamp" />
          <p class="notification-message text-grey-250 line-clamp">{{ message }}</p>
        </div>
      </div>

      <div class="md:opacity-0 group-hover:opacity-100">
        <ButtonModule
          prefix="icon-close"
          :has-hover="true"
          class="absolute right-10 top-45 text-14 text-grey-300 cursor-pointer"
          @click="deleteNotification"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, nextTick, onMounted, ref } from 'vue'

import userColorMixin from '@/mixins/userColorMixin'
import { parseDate } from '@/plugins/filters'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import { useStore } from 'vuex'

import notificationCodes from './NotificationCodes'
import notificationDescriptions from './NotificationDescriptions'
import { useRouter } from 'vue-router'

export default defineComponent({
  components: {
    ButtonModule
  },

  props: {
    notification: Object,
    hasBorder: Boolean
  },

  setup (props) {
    const { getters, dispatch } = useStore()
    const router = useRouter()

    const descriptionRef = ref(null)

    const { userColor } = userColorMixin(props.notification.teamMember?.user?.profile?.color)

    const isTwilightTheme = computed(() => getters.isTwilightTheme)
    const isDarkMode = computed(() => getters.isDarkMode)

    const title = computed(() => notificationCodes[props.notification.title])

    function getTextContent (text) {
      if (text) {
        var span = document.createElement('span')
        span.innerHTML = text.replace(/(<([^>]+)>)/ig, '\n') || ''
        return span.textContent.trim() || span.innerText.trim()
      }
      return null
    }

    const message = computed(() => {
      const span = document.createElement('span')
      span.innerHTML = props.notification.message
      const message = span.textContent

      switch (props.notification.title) {
        case 1:
          return 'Review website changes'
        case 2:
          return `"${message?.replace(/(@)+/g, '') || getTextContent(props.notification.chat.messages[0]?.text)?.replace(/(@)+/g, '')}"`
        case 3:
          return `"${message?.replace(/(@)+/g, '') || getTextContent(props.notification.discussion.content)?.replace(/(@)+/g, '') || props.notification.discussion.title}"`
        case 4:
          return `"${getTextContent(props.notification.project?.body)?.replace(/(@)+/g, '') || ''}"`
        case 5:
          return `"${getTextContent(props.notification.event?.additionalInfo)?.replace(/(@)+/g, '') || props.notification.event.title}"`
        case 6:
          return `"${getTextContent(props.notification.comment.body)?.replace(/(@)+/g, '')}"`
        case 7:
          return 'Why don\'t you have a look?'
        case 8:
          return `"${getTextContent(props.notification.task.additionalInfo)?.replace(/(@)+/g, '') || props.notification.task.title}"`
        case 100:
          return 'Say hi! 👋'
        case 101:
          return 'Let\'s keep things organized.'
        case 102:
          return 'Let\'s go and have a look.'
        case 103:
          return 'Tell them your opinion!'
        case 104:
          return 'Curious? Have a look!'
        case 105:
          return 'That sounds better, right?'
        default:
          return 'Review website changes'
      }
    })

    onMounted(() => {
      nextTick(() => {
        const fn = notificationDescriptions[props.notification.title]

        if (fn) {
          fn({
            notification: props.notification,
            ref: descriptionRef.value,
            router: router
          })
        }
      })
    })

    const getTime = computed(() => {
      const minutes = (Date.now() - new Date(props.notification.createdAt)) / 1000 / 60
      if (minutes < 1) {
        return 'now'
      } else if (minutes < 2) {
        return '1 minute ago'
      } else if (minutes < 60) {
        return `${parseInt(minutes)} minutes ago`
      } else if (minutes / 60 < 2) {
        return '1 hour ago'
      } else if (minutes / 60 < 24) {
        return `${parseInt(minutes / 60)} hours ago`
      } else if (minutes / 60 > 24 && minutes / 60 / 24 < 8) {
        if (parseInt(minutes / 60 / 24) === 1) return `${parseInt(minutes / 60 / 24)} day ago`
        return `${parseInt(minutes / 60 / 24)} days ago`
      }
      return parseDate(props.notification.createdAt, 'DD-MM-YYYY')
    })

    const titleTime = computed(() => {
      return parseDate(props.notification.createdAt, 'DD-MM-YYYY hh:mm')
    })

    function deleteNotification () {
      dispatch('deleteNotification', {
        id: props.notification.id,
        category: props.notification.category
      })
    }

    return {
      // ref
      descriptionRef,
      // data
      userColor,
      // computed
      title,
      getTime,
      message,
      titleTime,
      isDarkMode,
      isTwilightTheme,
      // function
      deleteNotification
    }
  }
})
</script>

<style>
.notification-message * {
  font-size: 14px !important;
}
</style>
